"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { doExchange } from "@/app/exchanger/actions";
import { D } from "@/app/exchanger/dict";
import { fill } from "@/lib/i18n";
import type { Item } from "@/lib/cases";
import { fmt } from "@/lib/money";
import { useT } from "./lang";
import { OpenButton } from "./overlays";

type Owned = { id: number; value: number; it: Item };
type CatItem = Item & { id: string };
type Page = { total: number; page: number; pages: number; items: CatItem[] };

// Client mirrors of lib/exchange (that module pulls the DB, so it can't be imported here).
const MAX_GET = 20, MAX_GIVE = 50;
const RARITIES = ["#b0c3d9", "#5e98d9", "#4b69ff", "#8847ff", "#d32ce6", "#eb4b4b", "#e4ae39"] as const;
const CATS = ["all", "knives", "gloves", "rifles", "pistols", "smg", "heavy"] as const;
const PRICES: { label: string; min: number; max: number }[] = [
  { label: "", min: 0, max: 0 }, { label: "< 1", min: 0, max: 100 }, { label: "1 – 10", min: 100, max: 1000 },
  { label: "10 – 100", min: 1000, max: 10000 }, { label: "100 +", min: 10000, max: 0 },
];
const rarityKey = (r: string) => `r_${r.slice(1)}` as `r_${string}`;

export default function ExchangerDesk({ owned, balance, loggedIn }: { owned: Owned[]; balance: number; loggedIn: boolean }) {
  const t = useT(D);
  const label = (k: string) => (t as Record<string, string>)[k] ?? k;

  // ---- selection
  const [give, setGive] = useState<number[]>([]);
  const [get, setGet] = useState<CatItem[]>([]);
  const byId = useMemo(() => new Map(owned.map((o) => [o.id, o])), [owned]);
  const giving = give.flatMap((id) => byId.get(id) ?? []);
  const V = giving.reduce((s, o) => s + o.value, 0);
  const T = get.reduce((s, it) => s + it.p, 0);
  const delta = V - T;
  const short = delta < 0 && balance < -delta;
  const budget = V + balance - T;
  const toggleGive = (id: number) => setGive((g) => (g.includes(id) ? g.filter((x) => x !== id) : g.length < MAX_GIVE ? [...g, id] : g));
  const addGet = (it: CatItem) => setGet((g) => (g.length < MAX_GET ? [...g, it] : g));
  const getCount = (id: string) => get.filter((g) => g.id === id).length;

  // ---- your skins (client-side filters: the list is already on the page)
  const [oq, setOq] = useState("");
  const [osort, setOsort] = useState<"desc" | "asc">("desc");
  const [orar, setOrar] = useState("");
  const shown = useMemo(() => {
    const n = oq.trim().toLowerCase();
    return owned
      .filter((o) => (!n || o.it.n.toLowerCase().includes(n)) && (!orar || o.it.r === orar))
      .sort((a, b) => (osort === "desc" ? b.value - a.value : a.value - b.value));
  }, [owned, oq, osort, orar]);
  const ownedRarities = useMemo(() => RARITIES.filter((r) => owned.some((o) => o.it.r === r)), [owned]);
  const selectAll = () => setGive((g) => [...g, ...shown.map((o) => o.id).filter((id) => !g.includes(id))].slice(0, MAX_GIVE));

  // ---- market: server pages, appended on scroll; any filter change restarts at page 0
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<"desc" | "asc">("desc");
  const [cat, setCat] = useState<(typeof CATS)[number]>("all");
  const [rarity, setRarity] = useState("");
  const [price, setPrice] = useState(0);
  const [affordable, setAffordable] = useState(false);
  const pr = PRICES[price];
  const capMax = affordable ? Math.max(1, budget) : 0;
  const max = pr.max && capMax ? Math.min(pr.max, capMax) : pr.max || capMax;
  const filterKey = JSON.stringify([q, sort, cat, rarity, pr.min, max]);
  const [req, setReq] = useState({ key: filterKey, page: 0 });
  if (req.key !== filterKey) setReq({ key: filterKey, page: 0 });
  const [list, setList] = useState<CatItem[]>([]);
  const [meta, setMeta] = useState({ total: 0, pages: 1, loaded: -1 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ctl = new AbortController();
    const id = setTimeout(async () => {
      setLoading(true);
      const p = new URLSearchParams({ q, sort, cat, rarity, min: String(pr.min), max: String(max), page: String(req.page) });
      try {
        const r: Page = await (await fetch(`/exchanger/items?${p}`, { signal: ctl.signal })).json();
        setList((l) => (req.page === 0 ? r.items : [...l, ...r.items]));
        setMeta({ total: r.total, pages: r.pages, loaded: r.page });
        setLoading(false);
      } catch {}
    }, req.page === 0 ? 200 : 0);
    return () => { clearTimeout(id); ctl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- req carries the filter key; the values are read from that render
  }, [req]);

  const scroller = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || loading || meta.loaded >= meta.pages - 1) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setReq((r) => ({ ...r, page: meta.loaded + 1 })), { root: scroller.current, rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [loading, meta]);
  useEffect(() => { if (req.page === 0) scroller.current?.scrollTo({ top: 0 }); }, [req.key, req.page]);

  // ---- exchange + success animation
  const [tab, setTab] = useState<"give" | "get">("get");
  const [busy, setBusy] = useState(false);
  const [swap, setSwap] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState<number | null>(null);
  useEffect(() => { if (toast === null) return; const id = setTimeout(() => setToast(null), 4500); return () => clearTimeout(id); }, [toast]);

  async function go() {
    setBusy(true);
    setErr("");
    const r = await doExchange(giving.map((o) => o.id), get.map((it) => it.id));
    if ("error" in r) { setBusy(false); setErr(r.error === "login" ? t.login_title : r.error ?? ""); return; }
    const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
    setSwap(true);
    setTimeout(() => { setGive([]); setGet([]); setSwap(false); setBusy(false); setToast(r.drops.length); }, reduce ? 0 : 750);
  }

  const giveTray = (
    <Tray title={t.give} total={V} empty={loggedIn ? t.none_give : t.login_title} swap={swap} dir="out"
      items={giving.map((o) => ({ key: o.id, it: o.it, value: o.value, remove: () => toggleGive(o.id) }))}
      onClear={giving.length ? () => setGive([]) : undefined} clear={t.clear} remove={t.remove} />
  );
  const getTray = (
    <Tray title={t.get} total={T} empty={t.none_get} swap={swap} dir="in"
      items={get.map((it, k) => ({ key: `${it.id}-${k}`, it, value: it.p, remove: () => setGet((g) => g.filter((_, j) => j !== k)) }))}
      onClear={get.length ? () => setGet([]) : undefined} clear={t.clear} remove={t.remove} />
  );

  return (
    <div className="space-y-4">
      <div className="segmented xc-tabs w-full" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "give"} data-active={tab === "give"} onClick={() => setTab("give")} className="flex-1">
          {t.tab_give}{give.length > 0 && <span className="xc-pill">{give.length}</span>}
        </button>
        <button type="button" role="tab" aria-selected={tab === "get"} data-active={tab === "get"} onClick={() => setTab("get")} className="flex-1">
          {t.tab_get}{get.length > 0 && <span className="xc-pill">{get.length}</span>}
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        {/* ---- your skins */}
        <section className="xc-panel" data-off={tab !== "give" || undefined}>
          <header className="xc-head">
            <h2 className="xc-title">{t.your_skins}<span className="xc-sub">{owned.length}</span></h2>
            {loggedIn && owned.length > 0 && (
              <button type="button" onClick={give.length ? () => setGive([]) : selectAll} className="xc-link">{give.length ? t.deselect : t.select_all}</button>
            )}
          </header>
          {!loggedIn ? (
            <div className="xc-empty">
              <p>{t.login_title}</p>
              <OpenButton what="login" className="btn btn-accent h-11 px-6 text-sm">{t.login}</OpenButton>
            </div>
          ) : owned.length === 0 ? (
            <div className="xc-empty">
              <p>{t.empty_owned}</p>
              <Link href="/" className="btn btn-accent h-11 px-6 text-sm">{t.open_cases}</Link>
            </div>
          ) : (
            <>
              <div className="xc-filters">
                <SearchField value={oq} onChange={setOq} placeholder={t.search} />
                <select value={osort} onChange={(e) => setOsort(e.target.value as "asc" | "desc")} aria-label={t.sort_high} className="field h-10 px-2 text-sm">
                  <option value="desc">{t.sort_high}</option>
                  <option value="asc">{t.sort_low}</option>
                </select>
              </div>
              <RarityChips list={ownedRarities} value={orar} onChange={setOrar} all={t.rarity_all} label={(r) => label(rarityKey(r))} />
              <div className="xc-meta">
                <span>{fill(t.selected, { n: give.length })} · <span className="coin text-text">{fmt(V)}</span></span>
                {give.length >= MAX_GIVE && <span className="text-gold">{t.max_give}</span>}
              </div>
              <div className="xc-grid xc-scroll">
                {shown.map((o) => (
                  <Tile key={o.id} it={o.it} value={o.value} on={give.includes(o.id)} onClick={() => toggleGive(o.id)} />
                ))}
                {shown.length === 0 && <p className="col-span-full py-10 text-center text-sm text-muted">{t.no_results}</p>}
              </div>
            </>
          )}
        </section>

        {/* ---- market */}
        <section className="xc-panel" data-off={tab !== "get" || undefined}>
          <header className="xc-head">
            <h2 className="xc-title">{t.market}<span className="xc-sub">{meta.total.toLocaleString("en-US")}</span></h2>
            {loggedIn && (
              <label className="xc-toggle">
                <input type="checkbox" checked={affordable} onChange={(e) => setAffordable(e.target.checked)} />
                <span aria-hidden className="xc-switch" />{t.affordable}
              </label>
            )}
          </header>
          <div className="no-sb xc-cats" role="tablist">
            {CATS.map((c) => (
              <button key={c} type="button" role="tab" aria-selected={cat === c} onClick={() => setCat(c)} className="xc-cat">{label(`cat_${c}`)}</button>
            ))}
          </div>
          <div className="xc-filters">
            <SearchField value={q} onChange={setQ} placeholder={t.search} />
            <select value={sort} onChange={(e) => setSort(e.target.value as "asc" | "desc")} aria-label={t.sort_high} className="field h-10 px-2 text-sm">
              <option value="desc">{t.sort_high}</option>
              <option value="asc">{t.sort_low}</option>
            </select>
          </div>
          <div className="no-sb flex gap-1.5 overflow-x-auto pb-1">
            {PRICES.map((p, k) => (
              <button key={k} type="button" onClick={() => setPrice(k)} aria-pressed={price === k} className="xc-chip-btn">
                {k ? <span className="coin">{p.label}</span> : t.price_any}
              </button>
            ))}
          </div>
          <RarityChips list={RARITIES} value={rarity} onChange={setRarity} all={t.rarity_all} label={(r) => label(rarityKey(r))} />
          <div ref={scroller} className="xc-grid xc-scroll">
            {list.map((it) => {
              const n = getCount(it.id);
              return <Tile key={it.id} it={it} value={it.p} count={n} add onClick={() => addGet(it)} disabled={get.length >= MAX_GET} />;
            })}
            {!loading && list.length === 0 && <p className="col-span-full py-10 text-center text-sm text-muted">{t.no_results}</p>}
            <div ref={sentinel} className="col-span-full h-6 text-center text-xs text-dim">{loading ? t.loading : ""}</div>
          </div>
        </section>
      </div>

      {/* ---- trade bar */}
      <section className="xc-bar" data-static={!loggedIn || undefined} aria-live="polite">
        {giveTray}
        <div className="xc-mid">
          <div className={`text-center text-[13px] font-bold tnum ${delta > 0 ? "text-money" : delta < 0 ? "text-gold" : "text-muted"}`}>
            {delta > 0 ? fill(t.to_balance, { x: fmt(delta) }) : delta < 0 ? fill(t.from_balance, { x: fmt(-delta) }) : t.even}
          </div>
          {loggedIn ? (
            <button type="button" onClick={go} disabled={busy || get.length === 0 || short} className="btn btn-accent xc-go" data-busy={busy || undefined}>
              <SwapIcon />{busy ? t.exchanging : t.exchange}
            </button>
          ) : (
            <OpenButton what="login" className="btn btn-accent xc-go">{t.login}</OpenButton>
          )}
          <div className="text-center text-[11px] text-dim tnum">{t.balance} <span className="coin">{fmt(balance)}</span></div>
        </div>
        {getTray}
      </section>
      {short && <p className="text-sm font-semibold text-danger">{t.insufficient} <Link href="/payments" className="font-normal text-muted underline underline-offset-2">{t.top_up}</Link></p>}
      {err && <p className="outcome-pop text-sm font-semibold text-danger">{err}</p>}
      {get.length >= MAX_GET && <p className="text-xs text-dim">{t.max_get}</p>}

      {toast !== null && (
        <div className="xc-toast" role="status">
          <span className="xc-toast-ico" aria-hidden>✓</span>
          <span>{fill(t.done, { n: toast })}</span>
          <Link href="/inventory" className="font-bold text-accent">{t.inventory} →</Link>
        </div>
      )}
    </div>
  );
}

function Tile({ it, value, on, count = 0, add, onClick, disabled }: {
  it: Item; value: number; on?: boolean; count?: number; add?: boolean; onClick: () => void; disabled?: boolean;
}) {
  const [weapon, skin] = it.n.split(" | ");
  return (
    <button type="button" onClick={onClick} disabled={disabled && !on} aria-pressed={add ? undefined : on}
      className="xc-tile rarity-tile" data-on={on || count > 0 || undefined} style={{ "--rc": it.r } as React.CSSProperties} data-fx title={it.n}>
      <span className="xc-tile-top">
        {it.w ? <span className="xc-wear">{it.w}</span> : <span />}
        {add ? <span className="xc-add" aria-hidden>{count > 0 ? `×${count}` : "+"}</span> : <span className="xc-check" aria-hidden>✓</span>}
      </span>
      <img src={it.i} alt="" loading="lazy" draggable={false} />
      <span className="xc-tile-name"><span>{skin ? weapon : ""}</span><b>{skin ?? weapon}</b></span>
      <span className="coin xc-tile-price">{fmt(value)}</span>
    </button>
  );
}

function Tray({ title, total, items, empty, swap, dir, onClear, clear, remove }: {
  title: string; total: number; empty: string; swap: boolean; dir: "in" | "out"; clear: string; remove: string;
  items: { key: string | number; it: Item; value: number; remove: () => void }[]; onClear?: () => void;
}) {
  return (
    <div className="xc-tray" data-empty={items.length === 0 || undefined}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="kicker">{title} · {items.length}</span>
        {onClear && <button type="button" onClick={onClear} className="xc-link">{clear}</button>}
      </div>
      <div className="coin font-display text-xl font-extrabold">{fmt(total)}</div>
      <div className="no-sb xc-tray-row">
        {items.length === 0 && <span className="text-xs text-dim">{empty}</span>}
        {items.map((x, k) => (
          <button key={x.key} type="button" onClick={x.remove} title={`${remove}: ${x.it.n} · ${fmt(x.value)}`} aria-label={`${remove}: ${x.it.n}`}
            className={`xc-chip rarity-tile ${swap ? `xc-fly-${dir}` : ""}`} style={{ "--rc": x.it.r, animationDelay: `${Math.min(k, 8) * 40}ms` } as React.CSSProperties}>
            <img src={x.it.i} alt="" draggable={false} /><span aria-hidden>×</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SearchField({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <label className="relative min-w-0 flex-1">
      <svg viewBox="0 0 24 24" width="16" height="16" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-dim" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder} className="field h-10 w-full pl-9 pr-3 text-sm" />
    </label>
  );
}

function RarityChips({ list, value, onChange, all, label }: { list: readonly string[]; value: string; onChange: (v: string) => void; all: string; label: (r: string) => string }) {
  if (list.length < 2) return null;
  return (
    <div className="no-sb flex gap-1.5 overflow-x-auto pb-1">
      <button type="button" onClick={() => onChange("")} aria-pressed={!value} className="xc-chip-btn">{all}</button>
      {list.map((r) => (
        <button key={r} type="button" onClick={() => onChange(value === r ? "" : r)} aria-pressed={value === r} className="xc-chip-btn" style={{ "--rc": r } as React.CSSProperties}>
          <span className="xc-dot" />{label(r)}
        </button>
      ))}
    </div>
  );
}

const SwapIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="xc-swap-ico" aria-hidden>
    <path d="M7 4 3 8l4 4M3 8h14M17 20l4-4-4-4M21 16H7" />
  </svg>
);




